/**
 * @file shop-time.ts
 * @description Время магазина в подписях кабинета (phase19.13 шаги 5-6;
 * phase19.31 — пояс из данных магазина, SC31–SC32).
 *
 * События доставки приходят в UTC. Если рисовать их временем браузера,
 * отчёт по инциденту у сотрудника в другом часовом поясе разойдётся с
 * отчётом коллеги на час-два. Поэтому ленты печатаются в одной зоне — зоне
 * МАГАЗИНА — и подписываются явно.
 *
 * Зона — свойство магазина (`shop.timeZone` из `/auth/me`): её ставит каркас
 * кабинета магазина (`setShopTimeZone`) при выборе магазина вкладки, до
 * отрисовки страниц. Функции форматирования остаются обычными функциями —
 * их вызывают компоненты и чистые модули без доступа к React-контексту.
 */

/**
 * Зона, пока магазин не выбран (и запасная при неизвестной зоне): прежнее
 * поведение существующих магазинов (SC32).
 */
export const DEFAULT_SHOP_TIME_ZONE = "Asia/Yekaterinburg";

let shopTimeZone = DEFAULT_SHOP_TIME_ZONE;

/** Зона, которую понимает браузер; иначе — запасная. */
function normalizeTimeZone(zone: string | null | undefined): string {
  if (!zone) return DEFAULT_SHOP_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("ru-RU", { timeZone: zone });
    return zone;
  } catch {
    return DEFAULT_SHOP_TIME_ZONE;
  }
}

/** Поставить зону магазина вкладки (каркас кабинета магазина). */
export function setShopTimeZone(zone: string | null | undefined): void {
  shopTimeZone = normalizeTimeZone(zone);
}

/** Текущая зона магазина вкладки (IANA). */
export function getShopTimeZone(): string {
  return shopTimeZone;
}

/** Смещение зоны для подписи: `UTC+5`, `UTC+3`, `UTC−3:30`, `UTC`. */
export function timeZoneOffsetLabel(zone: string, at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName")?.value;
  // «GMT+5» → «UTC+5»; «GMT» — это UTC без смещения.
  if (!part || part === "GMT") return "UTC";
  return part.replace("GMT", "UTC").replace("-", "−");
}

/** Подпись под лентой — чтобы «19:40» нельзя было прочитать двусмысленно. */
export function shopTimeZoneNote(zone: string = shopTimeZone): string {
  return `Время магазина (${timeZoneOffsetLabel(zone)})`;
}

/** `2026-08-14T14:40:00Z` → `14.08.2026, 19:40`. */
export function formatShopDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("ru-RU", {
    timeZone: shopTimeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Только дата: `14.08.2026` — день по поясу магазина (реестры документов). */
export function formatShopDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("ru-RU", {
    timeZone: shopTimeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Только время: `19:40`. Для колонки ленты, где дата уже в заголовке дня. */
export function formatShopTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("ru-RU", {
    timeZone: shopTimeZone,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
